import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { SectionHeader } from "@/components/sections/Services";
import { HOME, FAQ } from "@/constants/testIds";

export const faqGroups = [
  {
    category: "Getting Started",
    items: [
      {
        q: "How do I get started with MonetiseUP?",
        a: "Getting started is easy! Simply contact our team at contact@monetiseup.com and we'll guide you through the onboarding process. Our team will help you set up your account and integrate our ad technology into your platform.",
      },
      {
        q: "What are the minimum requirements to join?",
        a: "We work with publishers of all sizes. Whether you're just starting out or have millions of monthly visitors, we have solutions that can help maximize your revenue. Contact us at contact@monetiseup.com to discuss your specific requirements.",
      },
      {
        q: "How long does the setup process take?",
        a: "The setup process typically takes 24-48 hours once you provide the necessary information. Our technical team will handle the integration and ensure everything is working optimally before going live.",
      },
    ],
  },
  {
    category: "Revenue & Payments",
    items: [
      {
        q: "What are your payment terms?",
        a: "We offer NET 60 payment terms with monthly payments. Payments are processed automatically and you'll receive detailed revenue reports. For specific payment questions, contact our finance team at contact@monetiseup.com.",
      },
      {
        q: "What eCPM rates can I expect?",
        a: "eCPM rates vary based on your traffic quality, geography, and content vertical. Our average eCPM is $4.50, but many publishers see higher rates. Contact us at contact@monetiseup.com for a personalized revenue estimate.",
      },
      {
        q: "Is there a minimum payout threshold?",
        a: "Yes, our minimum payout threshold is $100. Once you reach this amount, payments are processed automatically according to our NET 60 terms.",
      },
    ],
  },
  {
    category: "Technical Support",
    items: [
      {
        q: "How do I integrate MonetiseUP ads?",
        a: "Our technical team provides complete integration support including code implementation, testing, and optimization. We support various integration methods including header bidding, direct integration, and programmatic connections. Contact our technical team at contact@monetiseup.com for assistance.",
      },
      {
        q: "Do you provide analytics and reporting?",
        a: "Yes! We provide comprehensive real-time analytics including impressions, clicks, revenue, eCPM, and fill rates. You'll have access to our publisher dashboard with detailed reporting and insights.",
      },
      {
        q: "What ad formats do you support?",
        a: "We support all major ad formats including display banners, native ads, video ads (pre-roll, mid-roll, outstream), mobile ads, and rich media formats. Our team can help you choose the best formats for your audience.",
      },
    ],
  },
  {
    category: "Account Management",
    items: [
      {
        q: "Do I get a dedicated account manager?",
        a: "Yes! All our publishers are assigned a dedicated account manager who will help optimize your revenue, provide insights, and ensure you're getting the best performance from our platform.",
      },
      {
        q: "How can I optimize my ad performance?",
        a: "Our team provides ongoing optimization including ad placement recommendations, format testing, and audience targeting improvements. Your account manager will work with you to continuously improve performance.",
      },
      {
        q: "Can I control which ads appear on my site?",
        a: "We provide comprehensive brand safety controls, category blocking, and advertiser filtering. You have full control over what ads appear on your platform.",
      },
    ],
  },
  {
    category: "Advertisers",
    items: [
      {
        q: "How do I start advertising with MonetiseUP?",
        a: "To start your advertising campaign, contact our advertising team at contact@monetiseup.com. We'll discuss your campaign objectives, target audience, and budget to create a customized advertising strategy.",
      },
      {
        q: "What targeting options are available?",
        a: "We offer comprehensive targeting including demographic, geographic, behavioral, contextual, and interest-based targeting. Our platform also supports lookalike audiences and custom audience segments.",
      },
      {
        q: "What is your minimum advertising budget?",
        a: "We work with advertisers of all sizes and budgets. Contact our team at contact@monetiseup.com to discuss your specific budget and campaign requirements.",
      },
    ],
  },
];

function flat(groups) {
  const out = [];
  groups.forEach((g) =>
    g.items.forEach((it) => out.push({ ...it, category: g.category })),
  );
  return out;
}

export default function Faq() {
  const flatItems = flat(faqGroups);
  const [activeCat, setActiveCat] = useState(faqGroups[0].category);
  const [openIdx, setOpenIdx] = useState(0);

  const filtered = flatItems.filter((it) => it.category === activeCat);

  return (
    <section
      id="faq"
      data-testid={HOME.faqSection}
      className="relative py-32"
    >
      <div className="container-x">
        <SectionHeader
          align="center"
          eyebrow="Help & Support"
          title={
            <>
              Frequently
              <br />
              <span className="aurora-text">Asked Questions</span>
            </>
          }
          subtitle="Find quick answers about onboarding, payments, integrations, and campaign management."
        />

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {faqGroups.map((g) => (
            <button
              key={g.category}
              onClick={() => {
                setActiveCat(g.category);
                setOpenIdx(0);
              }}
              data-testid={`faq-cat-${g.category.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                activeCat === g.category
                  ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF] text-white"
                  : "border border-white/10 text-white/60 hover:text-white hover:border-white/30"
              }`}
            >
              {g.category}
            </button>
          ))}
        </div>

        <div className="max-w-3xl mx-auto">
          {filtered.map((it, i) => (
            <div
              key={it.q}
              data-testid={FAQ.item(i)}
              className="border-b border-white/10"
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 py-6 text-left group"
              >
                <span className="font-display text-lg md:text-xl font-medium pr-4">
                  {it.q}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-[#00F0FF] shrink-0 transition-transform ${
                    openIdx === i ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {openIdx === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="text-white/65 pb-6 pr-10 leading-relaxed">
                      {it.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
